import type { TranslationCatalog } from './types';

export const zhHans: TranslationCatalog = {
  localeName: '中文',
  dir: 'ltr',
  brand: { wordmark: 'please.repair', product: 'Please.repair' },
  nav: {
    signIn: '登录',
    tenants: '租客专区',
    advocates: '协助者专区',
    landlords: '业主与中介专区',
    joinWaitlist: '加入候补名单',
    dashboard: '仪表盘', // drafted — not in design corpus
    knowledge: '知识库', // drafted — not in design corpus
  },
  hero: {
    title: '租房维修，办得妥妥当当。',
    subtitle:
      'Please.repair 跟踪每一项维修请求，从首次报修到完工。一旦停滞，它会指引你在所在州或领地走出下一步法律程序。',
    cta: '加入候补名单',
  },
  audiences: {
    tenant: {
      title: '我是租客',
      blurb: '报告问题，保存证据，若无人处理也知道该怎么办。',
    },
    advocate: {
      title: '我在帮助租客',
      blurb: '朋友、家人、社工或法律中心。陪他们一起处理，沉着而合规。',
    },
    landlord: {
      title: '我是业主或物业管理者',
      blurb: '所有请求集中处理，完整的书面记录让你合规经营，远离仲裁庭。',
    },
  },
  waitlist: {
    heading: '抢先使用 Please.repair',
    emailLabel: '你的邮箱',
    submit: '加入候补名单',
    success: '已登记成功，稍后联系。',
    finePrint: '不发垃圾邮件，大约每月一封。',
  },
  signIn: {
    heading: '登录或创建账户',
    stages: ['邮箱', '验证', '协议', '角色', '物业'],
    comingNote: '完整应用即将上线。当前展示的是预览版。', // drafted — not in design corpus
  },
  roles: {
    tenant: '租客',
    advocate: '协助者',
    propertyManager: '物业管理者',
    owner: '业主',
    tradie: '维修师傅',
  },
  statuses: {
    open: '进行中',
    acknowledged: '已确认',
    scheduled: '已排期',
    awaitingQuote: '待报价',
    quoted: '已报价',
    overdue: '已逾期',
    escalated: '已升级',
    resolved: '已解决',
  },
  severities: { urgent: '紧急', high: '较高', routine: '常规' },
  dashboard: {
    title: '全组件仪表盘',
    kpiOpen: '未结案件',
    kpiDueWeek: '本周到期',
    kpiOverdue: '已逾期',
    kpiResolved30: '已解决 · 30 天',
    filterAll: '全部',
    filterOpen: '进行中',
    filterDueSoon: '将到期',
    filterOverdue: '已逾期',
    searchPlaceholder: '搜索案件、物业、人员',
    emptyQueue: '这里还没有内容',
    timelineHeading: '时间线',
    deadlineHeading: '法定期限',
    sampleDataBadge: '示例数据', // drafted — not in design corpus
    chartTitle: '按严重程度划分的未结案件', // drafted — not in design corpus
    viewAsTable: '以表格查看', // drafted — not in design corpus
    viewAsChart: '以图表查看', // drafted — not in design corpus
  },
  knowledge: {
    heading: '知识库', // drafted — not in design corpus
    intro: '各州和领地的租房维修规则：紧急维修清单、维修时限、仲裁庭，以及可求助之处。', // ADR 0018 — neutral, matching clocksHeading
    disclaimer: 'Please.repair 负责整理事实、指引官方规则与表格，不提供法律意见。',
    lastVerified: '最近核验', // drafted — not in design corpus
    urgentHeading: '紧急维修', // drafted — not in design corpus
    clocksHeading: '维修时限',
    bodiesHeading: '仲裁庭与机构', // drafted — not in design corpus
    helpHeading: '求助渠道', // drafted — not in design corpus
    citationsHeading: '参考文献',
    verifyFlagsHeading: '核验标记', // drafted — not in design corpus
    clockStatutory: '法定 · {period}',
    clockBenchmark: '实践参考 · 约 {period}',
  },
  a11y: {
    skipToContent: '跳到主内容',
    opensInNewTab: '在新标签页打开',
    loading: '加载中…', // drafted — not in design corpus
  },
  errors: {
    generic: '出了点问题。', // drafted — not in design corpus
    retry: '重试', // drafted — not in design corpus
    notFound: '页面不存在', // drafted — not in design corpus
    notFoundBody: '该页面不存在或已移动。', // drafted — not in design corpus
    serverError: '出了点问题', // drafted — #376
    serverErrorBody: '无法显示此页面。请重试，或返回首页。', // drafted — #376
    offline: '当前处于离线状态', // drafted — #376
    offlineBody: '加载此页面需要网络连接。请重新连接后再试。', // drafted — #376
  },
  meta: {
    home: {
      title: 'Please.repair —— 有据可查的租房维修',
      description:
        'Please.repair —— 有据可查的租房维修。报告问题、保存证据、跟踪维修期限，并协调各方。',
    },
    tenants: {
      title: '租客专区 —— Please.repair',
      description: '报告问题，保存证据，若无人处理也知道该怎么办。',
    },
    advocates: {
      title: '协助者专区 —— Please.repair',
      description: '朋友、家人、社工或法律中心。陪他们一起处理，沉着而合规。',
    },
    landlords: {
      title: '业主与中介专区 —— Please.repair',
      description: '所有请求集中处理，完整的书面记录让你合规经营，远离仲裁庭。',
    },
    signIn: {
      title: '登录 —— Please.repair',
      description: '登录或创建你的 Please.repair 账户。',
    },
    dashboard: {
      title: '仪表盘 —— Please.repair',
      description: '未结案件、期限和近期动态，尽在一处队列。',
    },
    knowledge: {
      title: '各州与领地的租房维修规则 —— Please.repair',
      description: '各州和领地的租房维修规则：紧急维修清单、维修时限、仲裁庭，以及可求助之处。',
    },
    knowledgeDetail: {
      title: '租房维修规则 —— Please.repair',
      description: '按州和领地列出紧急维修清单、维修时限、仲裁庭以及可求助之处。',
    },
    offline: {
      title: '当前处于离线状态 —— Please.repair',
      description: '加载此页面需要网络连接。请重新连接后再试。',
    },
    serverError: {
      title: '出了点问题 —— Please.repair',
      description: '无法显示此页面。请重试，或返回首页。',
    },
    notFound: {
      title: '页面不存在 —— Please.repair',
      description: '该页面不存在或已移动。',
    },
  },
  footer: {
    disclaimer: 'Please.repair 负责整理事实、指引官方规则与表格，不提供法律意见。',
    rights: '© 2026 Aperim Pty Ltd · ABN 46 150 699 737',
  },
};
